// CAPABILITY: Viewer chat panel for public stage pages.
// Renders as part of the page layout (not a fixed overlay): a right-hand column
// on desktop and a stacked panel below the video on mobile, so the video resizes
// when chat is open. Mount once from StagePage inside the content flex row when
// eventData.chat.enabled and not in preview mode.
import React, { useEffect, useState } from "react";
import { normalizeChatSettings } from "../chatConfig";
import { useChatStream } from "../hooks/useChatStream";
import { getChatViewerId } from "../utils/chatIdentity";
import { getChatOpen, setChatOpen } from "../utils/chatVisibility";
import { getReadableTextColor } from "../utils/readableText";
import ChatComposer from "./ChatComposer";
import ChatMessageList from "./ChatMessageList";
import ChatNameModal from "./ChatNameModal";

export default function ChatSidebar({ stage }) {
  const pageId = stage?.id;
  const chat = normalizeChatSettings(stage?.chat);
  const accentColor = stage?.colors?.header || "#0f172a";
  const accentText = getReadableTextColor(accentColor);

  const [open, setOpen] = useState(() => getChatOpen(true));
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const viewerId = getChatViewerId();

  const { messages, sending, displayName, send, updateName } = useChatStream({
    pageId,
    enabled: Boolean(pageId) && chat.enabled,
  });

  useEffect(() => {
    setChatOpen(open);
  }, [open]);

  if (!pageId || !chat.enabled) return null;

  // Require a name before the first send: open the name modal, keep the typed
  // text in the composer, and let the viewer press Send again once named.
  const handleSend = async (text) => {
    if (!displayName) {
      setNameModalOpen(true);
      return { ok: false, status_label: "needs_name" };
    }
    return send(text);
  };

  const handleNameSubmit = async (name) => updateName(name);

  // Collapsed: a small reopen control that doesn't take layout space.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold shadow-xl ring-1 ring-black/5 transition hover:brightness-110"
        style={{ backgroundColor: accentColor, color: accentText }}
      >
        <span className="text-base leading-none">💬</span>
        Live chat
        {messages.length > 0 && (
          <span className="rounded bg-black/10 px-1.5 py-0.5 text-xs tabular-nums">
            {messages.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <aside className="flex h-[55dvh] min-h-0 w-full flex-col border-t border-slate-200 bg-white lg:h-auto lg:w-[380px] lg:border-l lg:border-t-0">
      {/* Header — mirrors StageNavBar's px-5 py-4 two-line layout for equal height. */}
      <div
        className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4"
        style={{ backgroundColor: accentColor, color: accentText }}
      >
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate text-sm font-semibold opacity-75">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            Live chat
          </p>
          <button
            type="button"
            onClick={() => setNameModalOpen(true)}
            className="truncate text-lg font-semibold tracking-tight transition hover:opacity-80"
            title="Set or edit your display name"
          >
            {displayName || "Set your name"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition hover:bg-black/10"
          aria-label="Hide chat"
          title="Hide chat"
          style={{ color: accentText }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      <ChatMessageList
        messages={messages}
        viewerId={viewerId}
        accentColor={accentColor}
        accentText={accentText}
      />

      <ChatComposer
        maxChars={chat.max_message_chars}
        sending={sending}
        onSend={handleSend}
        accentColor={accentColor}
        accentText={accentText}
      />

      <ChatNameModal
        isOpen={nameModalOpen}
        initialName={displayName}
        accentColor={accentColor}
        onClose={() => setNameModalOpen(false)}
        onSubmit={handleNameSubmit}
      />
    </aside>
  );
}
